import { Box, IconButton, OsTextField, SxProps } from "@outschool/backpack";
import { TextMimeType } from "@outschool/gql-frontend-generated";
import { faPaperPlane } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import {
  AttachmentGallery,
  ErrorMessage,
} from "@outschool/ui-components-shared";
import { validOptimisticResponse } from "@outschool/ui-utils";
import React, { useEffect, useRef, useState } from "react";

import type {
  AddPrivateClassMessageMutation,
  AttachmentFragmentFragment,
} from "..";
import { PrivateClassMessageFragment } from "../graphql/fragments/PrivateClassMessage";
import { addPrivateClassMessageMutation } from "../graphql/mutations/PrivateClassMessage";
import { useEnterToSend } from "../hooks/useEnterToSend";
import EnterToSendHint from "./EnterToSendHint";
import InlineEditableAttachments from "./InlineEditableAttachments";

export interface MessageSender {
  uid: string;
  name: string;
  photo: string | null;
  isLearner: boolean;
}

export interface MessageDraft {
  text: string;
  attachments: AttachmentFragmentFragment[];
  video: AttachmentFragmentFragment | null;
}

export interface OptimisticThreadCreatedPayload {
  threadUid: string;
  classPostUid?: string;
  learningAssignmentClassPostUid?: string;
  draft: MessageDraft;
  optimisticMessage: AddPrivateClassMessageMutation["addPrivateClassMessage"];
}

export interface ThreadCreateFailedPayload {
  threadUid: string;
  draft: MessageDraft;
  error: unknown;
}

interface BaseMessageInputProps {
  placeholder: string;
  sectionUid: string;
  learnerUid: string;
  sender: MessageSender;
  threadUid?: string;
  disabled?: boolean;
  onMessageSending?: () => void;
  onMessageSent?: () => void;
  onThreadCreated?: (threadUid: string) => void;
  onOptimisticThreadCreated?: (payload: OptimisticThreadCreatedPayload) => void;
  onThreadCreateFailed?: (payload: ThreadCreateFailedPayload) => void;
  initialDraft?: MessageDraft | null;
  initialError?: unknown;
  sx?: SxProps;
}

export type MessageInputProps = BaseMessageInputProps &
  (
    | { classPostUid: string; learningAssignmentClassPostUid?: never }
    | { classPostUid?: never; learningAssignmentClassPostUid: string }
    | { classPostUid?: never; learningAssignmentClassPostUid?: never }
  );

type ReadFieldFn = <T>(fieldName: string, ref: unknown) => T | undefined;

export default function MessageInput({
  placeholder,
  sectionUid,
  learnerUid,
  sender,
  threadUid,
  disabled = false,
  classPostUid,
  learningAssignmentClassPostUid,
  onMessageSending,
  onMessageSent,
  onThreadCreated,
  onOptimisticThreadCreated,
  onThreadCreateFailed,
  initialDraft,
  initialError,
  sx,
}: MessageInputProps) {
  const { t } = useTranslation("ui-components-classroom\\MessageInput");
  const [text, setText] = useState(initialDraft?.text ?? "");
  const [attachments, setAttachments] = useState<AttachmentFragmentFragment[]>(
    initialDraft?.attachments ?? [],
  );
  const [video, setVideo] = useState<AttachmentFragmentFragment | null>(
    initialDraft?.video ?? null,
  );
  const [error, setError] = useState<unknown>(initialError ?? null);
  const [addPrivateClassMessage] = useMutation(addPrivateClassMessageMutation);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof initialDraft === "undefined") {
      return;
    }
    setText(initialDraft?.text ?? "");
    setAttachments(initialDraft?.attachments ?? []);
    setVideo(initialDraft?.video ?? null);
  }, [initialDraft]);

  useEffect(() => {
    if (typeof initialError === "undefined") {
      return;
    }
    setError(initialError);
  }, [initialError]);

  const hasContent =
    text.trim().length > 0 || attachments.length > 0 || video !== null;

  const handleDeleteAttachment = (
    attachment: { uid: string },
    _event: React.SyntheticEvent,
  ) => {
    if (disabled) {
      return;
    }
    if (video && video.uid === attachment.uid) {
      setVideo(null);
    } else {
      setAttachments(prev => prev.filter(a => a.uid !== attachment.uid));
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (disabled || !hasContent) {
      return;
    }
    setError(null);
    const sendAttachments = [...attachments] as AttachmentFragmentFragment[];
    const sendVideo = video;
    const sendDraft: MessageDraft = {
      text: trimmed,
      attachments: sendAttachments,
      video: sendVideo,
    };
    const optimisticIdSeed = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    const optimisticThreadUid = threadUid ?? `temp-thread-${optimisticIdSeed}`;
    const sentAt = new Date().toISOString();
    const optimisticMessage: AddPrivateClassMessageMutation["addPrivateClassMessage"] =
      {
        __typename: "PrivateClassMessage",
        uid: `temp-message-${optimisticIdSeed}`,
        thread_uid: optimisticThreadUid,
        sender: {
          __typename: "MessageSender",
          uid: sender.uid,
          name: sender.name,
          photo: sender.photo,
          avatar: null,
          isLearner: sender.isLearner,
        },
        messageContent: {
          __typename: "MessageContent",
          uid: `temp-content-${optimisticIdSeed}`,
          text: trimmed,
          htmlText: trimmed,
          originalMimeType: TextMimeType.TextPlain,
          attachments: sendAttachments.map(a => ({
            __typename: "Attachment" as const,
            ...a,
          })),
          imageAttachments: [],
          video: sendVideo
            ? { __typename: "Attachment" as const, ...sendVideo }
            : null,
          externalVideoUrl: null,
          editedAt: null,
        },
        sentAt,
      };

    setText("");
    setAttachments([]);
    setVideo(null);
    onMessageSending?.();
    if (!threadUid) {
      onOptimisticThreadCreated?.({
        threadUid: optimisticThreadUid,
        classPostUid,
        learningAssignmentClassPostUid,
        draft: sendDraft,
        optimisticMessage,
      });
    }

    try {
      const result = await addPrivateClassMessage({
        variables: {
          privateClassMessageInput: {
            sectionUid,
            learnerUid,
            threadUid,
            classPostUid,
            learningAssignmentClassPostUid,
            messageContentInput: {
              text: trimmed,
              textMimeType: TextMimeType.TextPlain,
              attachmentUids: sendAttachments.map(a => a.uid),
              videoUid: sendVideo?.uid,
            },
          },
        },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          addPrivateClassMessage: optimisticMessage,
        }),
        update(cache, { data: mutationData }) {
          if (!mutationData?.addPrivateClassMessage) {
            return;
          }
          const newMessage = mutationData.addPrivateClassMessage;
          const targetThreadUid = threadUid ?? newMessage.thread_uid;
          const newRef = cache.writeFragment({
            fragment: PrivateClassMessageFragment,
            fragmentName: "PrivateClassMessageFragment",
            data: newMessage,
          });
          cache.modify({
            id: cache.identify({
              __typename: "Thread",
              uid: targetThreadUid,
            }),
            fields: {
              messages(
                existing = [],
                {
                  readField,
                }: {
                  readField: ReadFieldFn;
                },
              ) {
                const hasMessage = existing.some(
                  (messageRef: unknown) =>
                    readField<string>("uid", messageRef) === newMessage.uid,
                );
                if (hasMessage) {
                  return existing;
                }
                return [...existing, newRef];
              },
              latestMessage(existing) {
                return newRef ?? existing;
              },
            },
          });
        },
      });

      onMessageSent?.();
      const newThreadUid = result.data?.addPrivateClassMessage?.thread_uid;
      if (!threadUid && newThreadUid && onThreadCreated) {
        onThreadCreated(newThreadUid);
      }
    } catch (err) {
      onThreadCreateFailed?.({
        threadUid: optimisticThreadUid,
        draft: sendDraft,
        error: err,
      });
      if (isMountedRef.current) {
        setText(trimmed);
        setAttachments(sendAttachments);
        setVideo(sendVideo);
        setError(err);
      }
      OsPlatform.captureError(err);
    }
  };

  const handleTextFieldKeyDown = useEnterToSend(handleSend);

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "neutral.100",
        ...((sx ?? {}) as Record<string, unknown>),
      }}
    >
      <ErrorMessage value={error} sx={{ marginBottom: 0 }} />
      {(attachments.length > 0 || video) && (
        <Box sx={{ padding: "1.2rem 1.6rem 0" }}>
          <AttachmentGallery
            video={video}
            attachments={attachments}
            onDelete={disabled ? undefined : handleDeleteAttachment}
            containerSx={{ marginBottom: 0 }}
            primaryAttachmentMaxHeight="120px"
          />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "0.8rem",
          padding: "0.75rem 1.2rem",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineEditableAttachments
            showVideoUpload={true}
            video={video}
            value={attachments}
            onChange={setAttachments}
            onVideoChanged={setVideo}
            disabled={disabled}
            sx={{ flexShrink: 0, color: "text.secondary" }}
          />
        </Box>
        <OsTextField
          ariaLabel={placeholder}
          size="xsmall"
          fullWidth
          multiline
          maxRows={4}
          disabled={disabled}
          placeholder={placeholder}
          value={text}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setText(e.target.value)
          }
          onKeyDown={handleTextFieldKeyDown}
        />
        <IconButton
          icon={faPaperPlane}
          aria-label={t("Send message")}
          onClick={handleSend}
          disabled={disabled || !hasContent}
          sx={{
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { backgroundColor: "primary.dark" },
            "&.Mui-disabled": {
              backgroundColor: "action.disabledBackground",
              color: "action.disabled",
            },
            width: 40,
            height: 40,
            flexShrink: 0,
          }}
        />
      </Box>
      <EnterToSendHint />
    </Box>
  );
}
